import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { resolvePhotoUrl } from "@/lib/img";
import { toast } from "sonner";
import QLogo from "@/components/QLogo";
import ShareButton from "@/components/ShareButton";
import { waLink } from "@/lib/contacts";
import { CheckCircle, WhatsappLogo } from "@phosphor-icons/react";

const TABS = [
  { id: "submissions", label: "My Vehicles" },
  { id: "requirements", label: "Requirements" },
];

// Seller-facing labels — maps every backend `status` value to a friendly label.
// Single source of truth: `status` is set by the admin acquisition state engine.
const STATUS_LABEL = {
  submitted: "Submitted",
  under_review: "Under Review",
  offer_sent: "Offer Sent",
  negotiating: "Negotiation In Progress",
  inspection_scheduled: "Inspection Scheduled",
  inspection_completed: "Inspection Completed",
  final_offer_sent: "Final Offer Ready",
  deal_confirmed: "Deal Confirmed",
  closed: "Closed",
  follow_up: "Follow Up Later",
  // Legacy values still in DB for older submissions:
  buyer_matching_active: "Under Review",
  live_offers_active: "Offer Sent",
  offer_finalized: "Deal Confirmed",
};

const PIPELINE = ["Submitted", "Offer", "Negotiation", "Inspection", "Closed"];

const STATUS_STAGE = {
  submitted: 0,
  under_review: 0,
  offer_sent: 1,
  negotiating: 2,
  inspection_scheduled: 3,
  inspection_completed: 3,
  final_offer_sent: 3,
  deal_confirmed: 4,
  closed: 4,
  follow_up: 0,
  // legacy
  buyer_matching_active: 0,
  live_offers_active: 1,
  offer_finalized: 4,
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} mins ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hrs ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtL(n) { return n ? `₹${(n / 100000).toFixed(1)}L` : "—"; }

export default function MyActivity() {
  const nav = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [matchesByReq, setMatchesByReq] = useState({});
  const [loading, setLoading] = useState(true);

  const loadAll = React.useCallback(async (silent = false) => {
    try {
      const [v, r, m] = await Promise.all([
        api.get("/vehicles/me?detail=list"),
        api.get("/requirements/me"),
        api.get("/matches/me"),
      ]);
      setSubmissions(v.data);
      setReqs(r.data);
      const grouped = {};
      for (const match of (m.data || [])) {
        if (!grouped[match.requirement_id]) grouped[match.requirement_id] = [];
        grouped[match.requirement_id].push(match);
      }
      setMatchesByReq(grouped);
      if (!silent) {
        api.post("/notifications/seen").catch((err) => console.error("mark-seen failed:", err));
      }
    } catch (err) {
      console.error("Activity load failed:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll(false);
    // Live sync with admin actions — 10s poll, but pause when tab is hidden
    // to save mobile data + battery (Indian network conditions).
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => loadAll(true), 10000);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { loadAll(true); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadAll]);

  return (
    <div className="min-h-screen pb-32" data-testid="activity-page">
      <header className="px-6 pb-2 qd-pt-safe-header flex items-center justify-between">
        <QLogo size={28} />
        <ShareButton testid="activity-share" title="Q Drives" text="Track my vehicle acquisition on Q Drives." />
      </header>
      <div className="px-6 pt-5 max-w-3xl mx-auto">
        <h1 className="qd-h1 text-3xl text-white mb-7">My Activity</h1>

        <div className="flex gap-2 mb-7">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              className={`qd-chip ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <div className="text-white/40 text-sm">Loading…</div>}

        {tab === "submissions" && (
          <div className="space-y-3" data-testid="submissions-list">
            {submissions.length === 0 && <Empty cta="Sell My Car" onCta={() => nav("/sell")} text="No vehicles submitted yet." />}
            {submissions.map((v) => (
              <div key={v.id} className="qd-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-display text-lg text-white tracking-tight">{v.make} {v.model}</div>
                    <div className="text-[11px] text-white/45 tracking-wider mt-0.5">{v.registration_number} · {v.year}</div>
                  </div>
                  <StatusBadge status={v.status} />
                </div>
                <div className="text-xs text-white/55 mt-3">
                  {v.variant && <>{v.variant} · </>}{v.fuel_type} · {v.transmission} · {(v.km_driven || 0).toLocaleString()} km
                </div>

                {v.photos && v.photos.length > 0 && (
                  <div className="flex gap-1.5 mt-3 overflow-x-auto" data-testid={`vehicle-photos-${v.id}`}>
                    {v.photos.slice(0, 5).map((p) => (
                      <div key={p} className="w-14 h-14 bg-[#0E0E0E] shrink-0 overflow-hidden">
                        <img src={resolvePhotoUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = "0.2"; }} />
                      </div>
                    ))}
                    {(v.photos_count ?? v.photos.length) > 5 && (
                      <div className="w-14 h-14 bg-[#0E0E0E] shrink-0 flex items-center justify-center text-[11px] text-white/55">
                        +{(v.photos_count ?? v.photos.length) - 5}
                      </div>
                    )}
                  </div>
                )}

                <VehiclePipeline status={v.status} />

                {v.current_admin_offer ? (
                  <DealershipOfferCard v={v} onAction={() => loadAll(true)} />
                ) : (
                  <div className="mt-4 pt-4 border-t border-white/8 flex items-center justify-between">
                    <div className="text-xs text-white/55">
                      {v.status === "submitted" ? "Acquisition specialist reviewing your vehicle." : "Awaiting dealership offer."}
                    </div>
                    <a
                      href={waLink(`Hi Q Drives, requesting priority review on my ${v.make} ${v.model} (${v.registration_number}).`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`priority-${v.id}`}
                      className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1.5"
                    >
                      <WhatsappLogo size={12} weight="fill" /> Priority
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "requirements" && (
          <div className="space-y-4" data-testid="requirements-list">
            {reqs.length === 0 && <Empty cta="Find a Car" onCta={() => nav("/buy")} text="No active requirements." />}
            {reqs.map((r) => {
              const matches = matchesByReq[r.id] || [];
              return <RequirementCard key={r.id} r={r} matches={matches} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function RequirementCard({ r, matches }) {
  const [expanded, setExpanded] = useState(false);
  const specs = [
    { k: "Make", v: (r.brands || [])[0] },
    { k: "Preferred Model", v: r.preferred_model },
    { k: "Body Type", v: (r.body_types || [])[0] },
    { k: "Fuel Type", v: (r.fuel_types || [])[0] },
    { k: "Transmission", v: (r.transmissions || [])[0] },
    { k: "Budget Range", v: `${fmtL(r.budget_min)} – ${fmtL(r.budget_max)}` },
  ].filter(s => s.v);

  return (
    <div className="qd-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="qd-overline">Sourcing Request</div>
        <span
          className="text-[10px] tracking-wide px-2 py-[3px] border"
          style={{
            color: r.status === "stale" ? "rgba(255,255,255,0.5)" : "#E55A5A",
            borderColor: r.status === "stale" ? "rgba(255,255,255,0.15)" : "rgba(225,29,29,0.35)",
            background: r.status === "stale" ? "transparent" : "rgba(225,29,29,0.06)",
          }}
        >
          {r.status === "stale" ? "Stale" : "Active"}
        </span>
      </div>
      <div className="font-display text-base text-white tracking-tight mb-3">
        {specs.map(s => s.v).slice(0, 3).join(" · ") || "Sourcing requirement"}
      </div>
      <div className="grid grid-cols-2 gap-y-1.5 text-xs">
        {specs.map(s => (
          <React.Fragment key={s.k}>
            <span className="text-white/40 tracking-wide text-[10px]">{s.k}</span>
            <span className="text-white/90 text-right">{s.v}</span>
          </React.Fragment>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-white/8">
        {matches.length === 0 ? (
          <div className="text-xs text-white/55">Sourcing specialist is monitoring inventory for you.</div>
        ) : (
          <>
            <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between text-left" data-testid={`req-toggle-${r.id}`}>
              <div className="font-display text-sm text-white tracking-tight">{matches.length} {matches.length === 1 ? "Match" : "Matches"} Found</div>
              <span className="text-[10px] text-white/45">{expanded ? "Hide" : "View"}</span>
            </button>
            {expanded && (
              <div className="mt-3 space-y-2">
                {matches.slice(0, 5).map((m) => (
                  <div key={m.id} className="bg-[#0E0E0E] border border-white/8 p-3">
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="font-display text-sm text-white">{m.vehicle?.make} {m.vehicle?.model}</div>
                      <MatchTag pct={m.match_pct} />
                    </div>
                    <div className="text-[11px] text-white/55 tracking-wide mt-1">
                      {m.vehicle?.year} · {m.vehicle?.fuel_type} · {m.vehicle?.transmission} · {fmtL(m.vehicle?.expected_price)}
                    </div>
                    {m.reasons && m.reasons.length > 0 && (
                      <div className="text-[11px] text-white/70 mt-2">
                        Matched on {m.reasons.slice(0, 3).join(", ").toLowerCase()}.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function matchLabel(pct) {
  if (pct >= 85) return "Exact Match";
  if (pct >= 70) return "Strong Match";
  return "Compatible Match";
}

function MatchTag({ pct }) {
  const label = matchLabel(pct);
  return (
    <span
      data-testid="match-tag"
      className="text-[10px] tracking-wide text-[#E55A5A] px-2 py-[3px] border border-[#FF1E1E]/35"
      style={{ background: "rgba(225,29,29,0.06)" }}
    >
      {label}
    </span>
  );
}

function VehiclePipeline({ status }) {
  const active = STATUS_STAGE[status] ?? 0;
  return (
    <div className="flex items-center gap-1.5 mt-4">
      {PIPELINE.map((stage, i) => {
        const reached = i <= active;
        return (
          <div key={stage} className="flex-1 min-w-0">
            <div className="h-[3px]" style={{ background: reached ? "#C20505" : "rgba(255,255,255,0.08)" }} />
            <div className={`text-[10px] mt-1.5 truncate ${reached ? "text-white/85" : "text-white/35"}`}>{stage}</div>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const label = STATUS_LABEL[status] || status;
  return (
    <span
      data-testid="status-badge"
      className="text-[10px] tracking-wide text-[#E55A5A] px-2 py-[3px] border border-[#FF1E1E]/35"
      style={{ background: "rgba(225,29,29,0.06)" }}
    >
      {label}
    </span>
  );
}

function Empty({ text, cta, onCta }) {
  return (
    <div className="qd-card p-10 text-center">
      <p className="text-sm text-white/60 mb-5">{text}</p>
      {cta && <button className="btn-primary" onClick={onCta}>{cta}</button>}
    </div>
  );
}


function DealershipOfferCard({ v, onAction }) {
  const [mode, setMode] = useState("idle"); // idle | counter | submitting
  const [counter, setCounter] = useState("");
  const [note, setNote] = useState("");

  const accept = async () => {
    if (!window.confirm(`Accept the dealership offer of ${fmtL(v.current_admin_offer)}?`)) return;
    setMode("submitting");
    try {
      await api.post(`/vehicles/${v.id}/respond-offer`, { action: "accept" });
      toast.success("Offer accepted. Our team will reach out to finalize.");
      onAction();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to accept offer");
      setMode("idle");
    }
  };

  const submitCounter = async () => {
    const n = parseInt(counter.replace(/\D/g, ""), 10);
    if (!n || n <= 0) { toast.error("Enter a valid amount"); return; }
    setMode("submitting");
    try {
      await api.post(`/vehicles/${v.id}/respond-offer`, {
        action: "counter",
        counter_amount: n,
        note: note.trim() || null,
      });
      toast.success(`Request sent · ${fmtL(n)}`);
      setMode("idle");
      setCounter("");
      setNote("");
      onAction();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit");
      setMode("idle");
    }
  };

  if (mode === "counter") {
    return (
      <div className="mt-4 pt-4 border-t border-white/8" data-testid={`counter-form-${v.id}`}>
        <div className="qd-overline mb-2">Request Higher Offer</div>
        <input
          data-testid={`counter-amount-${v.id}`}
          value={counter}
          onChange={(e) => setCounter(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") submitCounter(); }}
          inputMode="numeric"
          placeholder={`Your asking price (current offer ${fmtL(v.current_admin_offer)})`}
          className="qd-input-block mb-2"
        />
        <textarea
          data-testid={`counter-note-${v.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for the dealership team"
          className="qd-input-block min-h-[60px] mb-3"
        />
        <div className="flex gap-2">
          <button
            data-testid={`counter-submit-${v.id}`}
            onClick={submitCounter}
            className="btn-primary flex-1 text-xs py-2"
            disabled={mode === "submitting"}
          >
            {mode === "submitting" ? "Sending…" : "Send Request"}
          </button>
          <button
            data-testid={`counter-cancel-${v.id}`}
            onClick={() => { setMode("idle"); setCounter(""); setNote(""); }}
            className="btn-secondary text-xs py-2 px-4"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-white/8" data-testid={`dealership-offer-${v.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="qd-overline mb-1">Dealership Offer</div>
          <div className="font-display text-2xl font-bold text-[#22C55E]">{fmtL(v.current_admin_offer)}</div>
          <div className="text-[11px] text-white/45 mt-1">
            {v.last_contacted_at ? `Updated ${timeAgo(v.last_contacted_at)}` : "From Q Drives"}
          </div>
        </div>
        {v.seller_response && (
          <div className="text-right">
            <div className="text-[10px] text-white/40 tracking-wide">Your last response</div>
            <div className="text-[11px] text-white/75 mt-0.5">
              {v.seller_response.action === "counter"
                ? `Requested ${fmtL(v.seller_response.amount)}`
                : "Accepted"}
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`accept-offer-${v.id}`}
          onClick={accept}
          disabled={mode === "submitting"}
          className="btn-primary text-xs py-2"
        >
          Accept Offer
        </button>
        <button
          data-testid={`request-higher-${v.id}`}
          onClick={() => setMode("counter")}
          disabled={mode === "submitting"}
          className="btn-secondary text-xs py-2"
        >
          Request Higher Offer
        </button>
      </div>
    </div>
  );
}
